import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Search, Star, ShieldCheck, Palette, Camera, Megaphone, Gem,
  Lock, Layers, Infinity, ChevronRight, Sparkles, TrendingUp,
  ShoppingCart, Users, Package, DollarSign, ExternalLink, X, Eye,
  Wand2, MessageSquare, Target, Award, Trophy, BarChart3, Send, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { BACKEND_URL, API } from '@/lib/backendUrl';


// Category config
const CATEGORIES = {
  all: { label: 'All', icon: Sparkles, color: 'text-foreground' },
  '3d_artist': { label: '3D Artists', icon: Palette, color: 'text-teal-500' },
  photographer: { label: 'Photographers', icon: Camera, color: 'text-amber-500' },
  nft_artist: { label: 'NFT Artists', icon: Gem, color: 'text-purple-500' },
  influencer: { label: 'Influencers', icon: Megaphone, color: 'text-rose-500' },
};

const LICENSE_INFO = {
  exclusive: { label: 'Exclusive', icon: Lock, desc: 'Only one hotel can use this design', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  limited: { label: 'Limited', icon: Layers, desc: 'Limited copies available', color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  open: { label: 'Open', icon: Infinity, desc: 'Unlimited mints, artist earns royalty', color: 'bg-teal-500/10 text-teal-600 border-teal-500/20' },
};

// Preview images per listing category
const CATEGORY_IMAGES = {
  '3d_artist': [
    'https://images.unsplash.com/photo-1758448511255-ac2a24a135d7?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1758448755969-8791367cf5c5?w=600&h=400&fit=crop',
  ],
  photographer: [
    'https://images.unsplash.com/photo-1732817207359-7ea9d3ac795b?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1760915795916-23259dff13b0?w=600&h=400&fit=crop',
  ],
  nft_artist: [
    'https://static.prod-images.emergentagent.com/jobs/d699780f-c7a5-4ad0-b770-86b0be102b92/images/24d34bfc0a6ce3b6469bbf611ca02c9b9726f92c2d11cea3c71c99ee015b318c.png',
  ],
  influencer: [
    'https://images.pexels.com/photos/5379175/pexels-photo-5379175.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
};

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/d699780f-c7a5-4ad0-b770-86b0be102b92/images/08206bd7a1ad41146b38e361082f49ba6ef389bb2288a3a357bf132fd8243400.png';

function getListingImage(listing, index = 0) {
  if (listing.preview_images?.length > 0) return listing.preview_images[0];
  const catImgs = CATEGORY_IMAGES[listing.category] || CATEGORY_IMAGES['3d_artist'];
  return catImgs[index % catImgs.length];
}

// --- Purchase Dialog ---
function PurchaseDialog({ listing, open, onClose }) {
  const [form, setForm] = useState({ hotel_id: 'hotel_grand_marina', hotel_name: 'The Grand Marina Hotel', copies: 1 });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const license = LICENSE_INFO[listing?.license_type] || LICENSE_INFO.open;
  const LicenseIcon = license.icon;

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metaverse/creator-marketplace/listings/${listing.listing_id}/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Purchase failed');
      }
      const data = await res.json();
      setResult(data);
      toast.success('NFT Rights purchased successfully!');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!listing) return null;
  const total = listing.price_hermes * form.copies;
  const fee = Math.round(total * 0.01 * 10000) / 10000;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="purchase-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font">{result ? 'Purchase Complete' : 'Acquire NFT Rights'}</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
              <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <p className="font-semibold heading-font text-lg">Rights Acquired</p>
              <p className="text-sm text-muted-foreground mt-1">Order ID: <span className="mono text-xs">{result.order_id}</span></p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-muted rounded-lg p-3"><p className="text-muted-foreground text-xs">Paid</p><p className="font-bold mono">{result.total_hermes} HERMES</p></div>
              <div className="bg-muted rounded-lg p-3"><p className="text-muted-foreground text-xs">Protocol Fee</p><p className="font-bold mono">{result.protocol_fee_hermes} HERMES</p></div>
              <div className="bg-muted rounded-lg p-3"><p className="text-muted-foreground text-xs">Creator Earned</p><p className="font-bold mono">{result.creator_earnings_hermes} HERMES</p></div>
              <div className="bg-muted rounded-lg p-3"><p className="text-muted-foreground text-xs">License</p><p className="font-bold">{result.license_type}</p></div>
            </div>
            <Button onClick={onClose} className="w-full" data-testid="purchase-done-btn">Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-3 bg-muted/50 rounded-xl p-3">
              <img src={getListingImage(listing)} alt="" className="w-16 h-16 rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm heading-font truncate">{listing.title}</p>
                <p className="text-xs text-muted-foreground">{listing.creator_name}</p>
                <Badge variant="outline" className={`mt-1 text-[10px] ${license.color}`}><LicenseIcon className="w-3 h-3 mr-1" />{license.label}</Badge>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs">Hotel</Label>
                <Select value={form.hotel_id} onValueChange={v => setForm(p => ({ ...p, hotel_id: v, hotel_name: v === 'hotel_grand_marina' ? 'The Grand Marina Hotel' : v }))}>
                  <SelectTrigger data-testid="purchase-hotel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hotel_grand_marina">The Grand Marina Hotel</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {listing.license_type !== 'exclusive' && (
                <div>
                  <Label className="text-xs">Copies {listing.license_type === 'limited' ? `(${(listing.limited_copies || 0) - listing.copies_sold} remaining)` : ''}</Label>
                  <Input type="number" min={1} max={listing.license_type === 'limited' ? (listing.limited_copies || 1) - listing.copies_sold : 100}
                    value={form.copies} onChange={e => setForm(p => ({ ...p, copies: parseInt(e.target.value) || 1 }))}
                    data-testid="purchase-copies-input" />
                </div>
              )}
            </div>

            <div className="border-t pt-3 space-y-1">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Unit Price</span><span className="mono">{listing.price_hermes} HERMES</span></div>
              {form.copies > 1 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Copies</span><span>{form.copies}x</span></div>}
              <div className="flex justify-between text-xs"><span className="text-muted-foreground">Protocol Fee (1%)</span><span className="mono">{fee} HERMES</span></div>
              <div className="flex justify-between text-base font-bold pt-1 border-t"><span>Total</span><span className="mono">{total} HERMES</span></div>
            </div>

            <Button onClick={handlePurchase} disabled={loading} className="w-full h-11" data-testid="confirm-purchase-btn">
              {loading ? <Sparkles className="w-4 h-4 mr-2 animate-spin" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
              {loading ? 'Processing...' : `Pay ${total} HERMES`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Creator Card ---
function CreatorCard({ creator, onClick }) {
  const CatIcon = CATEGORIES[creator.category]?.icon || Sparkles;
  return (
    <button onClick={onClick} className="text-left w-full group" data-testid={`creator-card-${creator.creator_id}`}>
      <Card className="overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 border-border/60">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[hsl(var(--web3-primary))] to-[hsl(var(--web3-accent))] flex items-center justify-center text-white font-bold text-lg heading-font shrink-0">
              {creator.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="font-semibold heading-font text-sm truncate">{creator.name}</p>
                {creator.verified && <ShieldCheck className="w-3.5 h-3.5 text-teal-500 shrink-0" />}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <CatIcon className={`w-3 h-3 ${CATEGORIES[creator.category]?.color}`} />
                <span className="text-xs text-muted-foreground">{CATEGORIES[creator.category]?.label}</span>
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-500" />{creator.rating}</span>
                <span className="flex items-center gap-1"><Package className="w-3 h-3" />{creator.total_sales} sales</span>
                {creator.sbt_token_id && (
                  <span className="flex items-center gap-1 text-[hsl(var(--web3-primary))]">
                    <Gem className="w-3 h-3" />SBT #{creator.sbt_token_id}
                  </span>
                )}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0 mt-1" />
          </div>
        </CardContent>
      </Card>
    </button>
  );
}

// --- Listing Card ---
function ListingCard({ listing, index, onPurchase, onViewCreator }) {
  const license = LICENSE_INFO[listing.license_type] || LICENSE_INFO.open;
  const LicenseIcon = license.icon;
  const CatIcon = CATEGORIES[listing.category]?.icon || Sparkles;

  return (
    <Card className="overflow-hidden group transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 border-border/60" data-testid={`listing-card-${listing.listing_id}`}>
      <div className="relative h-44 bg-muted overflow-hidden">
        <img src={getListingImage(listing, index)} alt={listing.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <Badge variant="outline" className={`absolute top-3 left-3 ${license.color} backdrop-blur-sm text-[10px]`}>
          <LicenseIcon className="w-3 h-3 mr-1" />{license.label}
        </Badge>
        {listing.license_type === 'limited' && (
          <Badge className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-[10px]">
            {(listing.limited_copies || 0) - listing.copies_sold} left
          </Badge>
        )}
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-white font-semibold heading-font text-sm leading-tight line-clamp-2">{listing.title}</p>
        </div>
      </div>
      <CardContent className="p-4 space-y-3">
        <button onClick={() => onViewCreator?.(listing.creator_id)} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[hsl(var(--web3-primary))] to-[hsl(var(--web3-accent))] flex items-center justify-center text-white text-[10px] font-bold">
            {listing.creator_name?.charAt(0)}
          </div>
          <span className="text-xs text-muted-foreground">{listing.creator_name}</span>
          {listing.creator_verified && <ShieldCheck className="w-3 h-3 text-teal-500" />}
        </button>

        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{listing.description}</p>

        <div className="flex flex-wrap gap-1">
          {listing.tags?.slice(0, 3).map(t => (
            <Badge key={t} variant="secondary" className="text-[10px] px-1.5 py-0">{t}</Badge>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <div>
            <p className="text-xl font-bold mono heading-font">{listing.price_hermes.toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">HERMES</p>
          </div>
          <Button size="sm" onClick={() => onPurchase(listing)}
            disabled={listing.status === 'sold_out'}
            data-testid={`buy-btn-${listing.listing_id}`}
            className="h-9 px-4">
            {listing.status === 'sold_out' ? 'Sold Out' : 'Acquire Rights'}
          </Button>
        </div>
        {listing.license_type === 'open' && listing.royalty_pct > 0 && (
          <p className="text-[10px] text-muted-foreground text-center">{listing.royalty_pct}% royalty on guest resales</p>
        )}
      </CardContent>
    </Card>
  );
}

// --- Score Bar Component ---
function ScoreBar({ label, score, color = 'bg-teal-500' }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold mono">{score}/100</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${Math.min(100, score)}%` }} />
      </div>
    </div>
  );
}

// --- Star Rating Input ---
function StarRating({ value, onChange, size = 'w-5 h-5' }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <button key={s} type="button" onClick={() => onChange(s)}
          className="transition-transform hover:scale-110"
          data-testid={`star-${s}`}>
          <Star className={`${size} ${s <= value ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30'}`} />
        </button>
      ))}
    </div>
  );
}

// --- Review Submission Dialog ---
function ReviewDialog({ open, onClose, creatorId, creatorName }) {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState('');
  const [quality, setQuality] = useState(5);
  const [delivery, setDelivery] = useState(5);
  const [communication, setCommunication] = useState(5);
  const [text, setText] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    if (open && creatorId) {
      setLoadingOrders(true);
      fetch(`${API}/api/metaverse/creator-marketplace/creators/${creatorId}/orders`)
        .then(r => r.json())
        .then(data => { setOrders(data || []); setLoadingOrders(false); })
        .catch(() => setLoadingOrders(false));
    }
  }, [open, creatorId]);

  const handleSubmit = async () => {
    if (!selectedOrder) { toast.error('Select an order to review'); return; }
    setLoading(true);
    try {
      const order = orders.find(o => o.order_id === selectedOrder);
      const res = await fetch(`${API}/api/metaverse/creator-marketplace/creators/${creatorId}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: selectedOrder,
          hotel_id: order?.hotel_id || 'hotel_grand_marina',
          hotel_name: order?.hotel_name || 'Hotel',
          quality_rating: quality,
          delivery_rating: delivery,
          communication_rating: communication,
          review_text: text,
          reviewer_name: reviewerName,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success('Review submitted! Creator scores updated.');
      onClose(true); // true = refresh needed
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="sm:max-w-md" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font">Review {creatorName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Select Order</Label>
            {loadingOrders ? (
              <p className="text-xs text-muted-foreground py-2">Loading orders...</p>
            ) : orders.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">No orders found. Purchase from this creator first.</p>
            ) : (
              <Select value={selectedOrder} onValueChange={setSelectedOrder}>
                <SelectTrigger data-testid="review-order-select"><SelectValue placeholder="Choose an order..." /></SelectTrigger>
                <SelectContent>
                  {orders.map(o => (
                    <SelectItem key={o.order_id} value={o.order_id}>
                      {o.listing_title} ({o.total_hermes} HERMES)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="text-center space-y-1">
              <p className="text-[10px] text-muted-foreground">Quality</p>
              <StarRating value={quality} onChange={setQuality} size="w-4 h-4" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-[10px] text-muted-foreground">Delivery</p>
              <StarRating value={delivery} onChange={setDelivery} size="w-4 h-4" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-[10px] text-muted-foreground">Communication</p>
              <StarRating value={communication} onChange={setCommunication} size="w-4 h-4" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Your Review</Label>
            <Textarea value={text} onChange={e => setText(e.target.value)}
              placeholder="Share your experience working with this creator..."
              className="h-20 text-sm" data-testid="review-text-input" />
          </div>

          <div>
            <Label className="text-xs">Your Name (optional)</Label>
            <Input value={reviewerName} onChange={e => setReviewerName(e.target.value)}
              placeholder="Hotel Manager" data-testid="reviewer-name-input" />
          </div>

          <Button onClick={handleSubmit} disabled={loading || !selectedOrder} className="w-full h-10" data-testid="submit-review-btn">
            {loading ? <Sparkles className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {loading ? 'Submitting...' : 'Submit Review'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Creator Detail Panel ---
function CreatorDetailPanel({ creator, listings, onClose, onPurchase }) {
  const CatIcon = CATEGORIES[creator.category]?.icon || Sparkles;
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [detailTab, setDetailTab] = useState('overview');
  const [sbtData, setSbtData] = useState(null);

  useEffect(() => {
    if (creator?.creator_id) {
      fetch(`${API}/api/metaverse/creator-marketplace/creators/${creator.creator_id}/reviews`)
        .then(r => r.json())
        .then(data => setReviews(data || []))
        .catch(() => {});
      // Fetch full SBT data with sub-roles
      fetch(`${API}/api/metaverse/creator-marketplace/creators/${creator.creator_id}/scores`)
        .then(r => r.json())
        .then(data => setSbtData(data?.sbt || null))
        .catch(() => {});
    }
  }, [creator?.creator_id]);

  const handleReviewClose = (refreshNeeded) => {
    setShowReviewForm(false);
    if (refreshNeeded) {
      // Reload reviews + creator data
      fetch(`${API}/api/metaverse/creator-marketplace/creators/${creator.creator_id}/reviews`)
        .then(r => r.json())
        .then(data => setReviews(data || []))
        .catch(() => {});
    }
  };

  const trustColor = (creator.trust_score || 0) >= 80 ? 'text-emerald-500' : (creator.trust_score || 0) >= 50 ? 'text-amber-500' : 'text-muted-foreground';

  return (
    <>
    <Dialog open={!!creator} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="creator-detail-panel">
        <DialogHeader>
          <DialogTitle className="heading-font flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(var(--web3-primary))] to-[hsl(var(--web3-accent))] flex items-center justify-center text-white font-bold text-lg">
              {creator.name.charAt(0)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                {creator.name}
                {creator.verified && <ShieldCheck className="w-4 h-4 text-teal-500" />}
              </div>
              <div className="flex items-center gap-3 text-xs font-normal text-muted-foreground">
                <span className="flex items-center gap-1"><CatIcon className={`w-3 h-3 ${CATEGORIES[creator.category]?.color}`} />{CATEGORIES[creator.category]?.label}</span>
                {(creator.trust_score || 0) > 0 && (
                  <span className={`flex items-center gap-1 font-semibold ${trustColor}`}>
                    <Trophy className="w-3 h-3" />{creator.trust_score} Trust
                  </span>
                )}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground leading-relaxed">{creator.bio}</p>

        {/* Tabs inside detail panel */}
        <Tabs value={detailTab} onValueChange={setDetailTab}>
          <TabsList className="grid w-full grid-cols-3 h-9">
            <TabsTrigger value="overview" className="text-xs" data-testid="detail-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="scores" className="text-xs" data-testid="detail-tab-scores">Scores</TabsTrigger>
            <TabsTrigger value="reviews" className="text-xs" data-testid="detail-tab-reviews">Reviews ({reviews.length})</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4 mt-3">
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <Star className="w-3.5 h-3.5 text-amber-500 mx-auto mb-1" />
                <p className="text-base font-bold heading-font">{creator.rating}</p>
                <p className="text-[9px] text-muted-foreground">Rating</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <Package className="w-3.5 h-3.5 text-teal-500 mx-auto mb-1" />
                <p className="text-base font-bold heading-font">{creator.total_sales}</p>
                <p className="text-[9px] text-muted-foreground">Sales</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <DollarSign className="w-3.5 h-3.5 text-emerald-500 mx-auto mb-1" />
                <p className="text-base font-bold heading-font mono">{creator.total_earnings_hermes.toLocaleString()}</p>
                <p className="text-[9px] text-muted-foreground">Earned</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <MessageSquare className="w-3.5 h-3.5 text-purple-500 mx-auto mb-1" />
                <p className="text-base font-bold heading-font">{creator.total_reviews || 0}</p>
                <p className="text-[9px] text-muted-foreground">Reviews</p>
              </div>
            </div>

            {creator.specialties?.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">Specialties</p>
                <div className="flex flex-wrap gap-1.5">
                  {creator.specialties.map(s => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}
                </div>
              </div>
            )}

            <div>
              <p className="text-sm font-semibold heading-font mb-3">Listings ({listings.length})</p>
              <div className="grid gap-3">
                {listings.map((l, i) => (
                  <div key={l.listing_id} className="flex gap-3 bg-muted/50 rounded-xl p-3 hover:bg-muted transition-colors">
                    <img src={getListingImage(l, i)} alt="" className="w-20 h-16 rounded-lg object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm heading-font truncate">{l.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={`text-[10px] ${LICENSE_INFO[l.license_type]?.color}`}>{LICENSE_INFO[l.license_type]?.label}</Badge>
                        <span className="font-bold mono text-sm">{l.price_hermes.toLocaleString()} HERMES</span>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => onPurchase(l)} disabled={l.status === 'sold_out'}
                      className="shrink-0 self-center h-8" data-testid={`creator-panel-buy-${l.listing_id}`}>
                      {l.status === 'sold_out' ? 'Sold' : 'Buy'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Scores Tab */}
          <TabsContent value="scores" className="space-y-4 mt-3">
            {/* SBT Identity Card — always shown */}
            {creator.sbt_token_id && (
              <div className="bg-gradient-to-r from-[hsl(var(--web3-primary))]/10 to-[hsl(var(--web3-accent))]/10 border border-[hsl(var(--web3-primary))]/20 rounded-xl p-4" data-testid="creator-sbt-info">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Gem className="w-5 h-5 text-[hsl(var(--web3-primary))]" />
                    <div>
                      <p className="text-xs font-semibold heading-font">Person SBT</p>
                      <p className="text-[10px] text-muted-foreground">Unified Soulbound Identity</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold mono text-[hsl(var(--web3-primary))]">#{creator.sbt_token_id}</p>
                    <p className="text-[10px] text-muted-foreground">Base Sepolia</p>
                  </div>
                </div>
                {/* Active Roles */}
                <div className="flex items-center gap-2 mt-3">
                  <p className="text-[10px] text-muted-foreground">Roles:</p>
                  {(sbtData?.roles || creator.sbt_roles || ['creator']).map(role => {
                    const roleConfig = {
                      guest: { label: 'Guest', color: 'bg-blue-500/15 text-blue-500 border-blue-500/20' },
                      staff: { label: 'Staff', color: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/20' },
                      creator: { label: 'Creator', color: 'bg-purple-500/15 text-purple-500 border-purple-500/20' },
                    }[role] || { label: role, color: 'bg-muted text-foreground' };
                    return (
                      <Badge key={role} variant="outline" className={`text-[10px] ${roleConfig.color}`} data-testid={`sbt-role-${role}`}>
                        {roleConfig.label}
                      </Badge>
                    );
                  })}
                </div>
                {/* Sub-roles per active role */}
                {sbtData?.role_scores && (
                  <div className="mt-3 space-y-2">
                    {(sbtData.roles || []).map(role => {
                      const rs = sbtData.role_scores?.[role] || {};
                      const subKey = { guest: 'types', staff: 'positions', creator: 'specializations' }[role];
                      const subLabel = { guest: 'Guest Types', staff: 'Positions', creator: 'Specializations' }[role];
                      const subs = rs[subKey] || [];
                      if (subs.length === 0) return null;
                      return (
                        <div key={role} className="flex items-center gap-2 flex-wrap" data-testid={`sbt-subroles-${role}`}>
                          <span className="text-[10px] text-muted-foreground w-20 shrink-0">{subLabel}:</span>
                          {subs.map(s => (
                            <Badge key={s} variant="secondary" className="text-[10px] px-1.5 py-0">
                              {s.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Score values on SBT */}
                <div className="grid grid-cols-4 gap-2 mt-3 text-center border-t border-[hsl(var(--web3-primary))]/10 pt-3">
                  <div><p className="text-[10px] text-muted-foreground">quality</p><p className="text-xs font-semibold mono">{Math.round(creator.quality_score || 0)}</p></div>
                  <div><p className="text-[10px] text-muted-foreground">delivery</p><p className="text-xs font-semibold mono">{Math.round(creator.delivery_score || 0)}</p></div>
                  <div><p className="text-[10px] text-muted-foreground">comm</p><p className="text-xs font-semibold mono">{Math.round(creator.communication_score || 0)}</p></div>
                  <div><p className="text-[10px] text-muted-foreground">reviews</p><p className="text-xs font-semibold mono">{creator.total_reviews || 0}</p></div>
                </div>
              </div>
            )}

            {/* Trust Score breakdown */}
            {(creator.trust_score || 0) > 0 ? (
              <>
                <div className="text-center bg-muted rounded-xl p-5">
                  <Trophy className={`w-8 h-8 mx-auto mb-2 ${trustColor}`} />
                  <p className={`text-4xl font-bold heading-font mono ${trustColor}`}>{creator.trust_score}</p>
                  <p className="text-xs text-muted-foreground mt-1">Trust Score</p>
                </div>
                <div className="space-y-3">
                  <ScoreBar label="Quality (50%)" score={creator.quality_score || 0} color="bg-emerald-500" />
                  <ScoreBar label="Delivery (25%)" score={creator.delivery_score || 0} color="bg-teal-500" />
                  <ScoreBar label="Communication (25%)" score={creator.communication_score || 0} color="bg-blue-500" />
                </div>
                <p className="text-[10px] text-muted-foreground text-center">
                  Based on {creator.total_reviews || 0} review{(creator.total_reviews || 0) !== 1 ? 's' : ''} from hotel buyers
                </p>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-xs text-muted-foreground">No review scores yet. Scores build from hotel reviews after purchases.</p>
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={() => setShowReviewForm(true)} data-testid="write-review-btn">
              <Star className="w-4 h-4 mr-2" />Write a Review
            </Button>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="space-y-3 mt-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold heading-font">{reviews.length} Review{reviews.length !== 1 ? 's' : ''}</p>
              <Button size="sm" variant="outline" onClick={() => setShowReviewForm(true)} className="h-7 text-xs" data-testid="write-review-btn-2">
                <Star className="w-3 h-3 mr-1" />Write Review
              </Button>
            </div>
            {reviews.length === 0 ? (
              <div className="text-center py-6">
                <MessageSquare className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">No reviews yet. Be the first!</p>
              </div>
            ) : (
              reviews.map(r => (
                <div key={r.review_id} className="border border-border/60 rounded-xl p-3 space-y-2" data-testid={`review-${r.review_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-bold">
                        {(r.reviewer_name || 'H').charAt(0)}
                      </div>
                      <div>
                        <p className="text-xs font-semibold">{r.reviewer_name}</p>
                        <p className="text-[10px] text-muted-foreground">{r.listing_title}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} className={`w-3 h-3 ${s <= Math.round(r.overall_rating) ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/20'}`} />
                      ))}
                    </div>
                  </div>
                  {r.review_text && <p className="text-xs text-muted-foreground leading-relaxed">{r.review_text}</p>}
                  <div className="flex gap-3 text-[10px] text-muted-foreground">
                    <span>Quality: {r.quality_rating}/5</span>
                    <span>Delivery: {r.delivery_rating}/5</span>
                    <span>Comm: {r.communication_rating}/5</span>
                  </div>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>

    {/* Review Form Dialog */}
    <ReviewDialog open={showReviewForm} onClose={handleReviewClose}
      creatorId={creator.creator_id} creatorName={creator.name} />
    </>
  );
}

// --- LEADERBOARD COMPONENT ---
function LeaderboardTab({ onViewCreator }) {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = filterCat && filterCat !== 'all_categories' ? `?category=${filterCat}` : '';
    fetch(`${API}/api/metaverse/creator-marketplace/leaderboard${params}`)
      .then(r => r.json())
      .then(data => { setLeaders(data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filterCat]);

  const rankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-br from-amber-500 to-orange-600';
    if (rank === 2) return 'bg-gradient-to-br from-slate-300 to-slate-400';
    if (rank === 3) return 'bg-gradient-to-br from-amber-700 to-amber-800';
    return 'bg-muted';
  };
  const rankText = (rank) => rank <= 3 ? 'text-white font-bold' : 'text-foreground font-semibold';
  const trustColor = (score) => score >= 80 ? 'text-emerald-500' : score >= 50 ? 'text-amber-500' : 'text-muted-foreground';

  return (
    <div className="space-y-4" data-testid="leaderboard-content">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold heading-font flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" /> Creator Leaderboard
        </p>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="leaderboard-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_categories">All Categories</SelectItem>
            <SelectItem value="3d_artist">3D Artists</SelectItem>
            <SelectItem value="photographer">Photographers</SelectItem>
            <SelectItem value="nft_artist">NFT Artists</SelectItem>
            <SelectItem value="influencer">Influencers</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Sparkles className="w-6 h-6 text-amber-500 mx-auto mb-2 animate-pulse" />
          <p className="text-xs text-muted-foreground">Loading leaderboard...</p>
        </div>
      ) : leaders.length === 0 ? (
        <div className="text-center py-12">
          <Trophy className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No ranked creators yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {leaders.map(c => {
            const CatIcon = CATEGORIES[c.category]?.icon || Sparkles;
            return (
              <button key={c.creator_id} onClick={() => onViewCreator(c.creator_id)}
                className="w-full text-left group" data-testid={`leaderboard-row-${c.rank}`}>
                <Card className="overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-px border-border/60">
                  <CardContent className="p-3 flex items-center gap-3">
                    {/* Rank badge */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0 ${rankBg(c.rank)} ${rankText(c.rank)}`}>
                      {c.rank <= 3 ? <Crown className="w-4 h-4" /> : c.rank}
                    </div>

                    {/* Creator info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm heading-font truncate">{c.name}</span>
                        {c.verified && <ShieldCheck className="w-3.5 h-3.5 text-teal-500 shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <CatIcon className={`w-3 h-3 ${CATEGORIES[c.category]?.color}`} />
                        <span className="text-[10px] text-muted-foreground">{CATEGORIES[c.category]?.label}</span>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <MessageSquare className="w-2.5 h-2.5" />{c.total_reviews} reviews
                        </span>
                      </div>
                    </div>

                    {/* Score bars (compact) */}
                    <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="text-center w-12">
                        <p className="font-semibold text-foreground mono">{c.quality_score}</p>
                        <p className="text-[9px]">Quality</p>
                      </div>
                      <div className="text-center w-12">
                        <p className="font-semibold text-foreground mono">{c.delivery_score}</p>
                        <p className="text-[9px]">Delivery</p>
                      </div>
                      <div className="text-center w-12">
                        <p className="font-semibold text-foreground mono">{c.communication_score}</p>
                        <p className="text-[9px]">Comm</p>
                      </div>
                    </div>

                    {/* Trust score */}
                    <div className="text-center shrink-0">
                      <p className={`text-lg font-bold heading-font mono ${trustColor(c.trust_score)}`}>{c.trust_score}</p>
                      <p className="text-[9px] text-muted-foreground">Trust</p>
                    </div>

                    {/* SBT Badge with roles */}
                    {c.sbt_token_id && (
                      <div className="hidden sm:flex items-center gap-1.5 shrink-0">
                        <div className="flex items-center gap-1 bg-[hsl(var(--web3-primary))]/10 rounded-full px-2 py-1">
                          <Gem className="w-3 h-3 text-[hsl(var(--web3-primary))]" />
                          <span className="text-[10px] mono font-semibold text-[hsl(var(--web3-primary))]">#{c.sbt_token_id}</span>
                        </div>
                        {(c.sbt_roles || []).map(role => {
                          const roleColor = { guest: 'bg-blue-500/15 text-blue-500', staff: 'bg-emerald-500/15 text-emerald-500', creator: 'bg-purple-500/15 text-purple-500' }[role] || 'bg-muted';
                          return <span key={role} className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${roleColor}`}>{role}</span>;
                        })}
                      </div>
                    )}

                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


// --- AI MATCHING DIALOG ---
function AiMatchDialog({ open, onClose, onPurchase, onViewCreator }) {
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleMatch = async () => {
    if (!description.trim()) { toast.error('Describe what your hotel needs'); return; }
    setLoading(true);
    setResult(null);
    try {
      const body = { description: description.trim() };
      if (budget) body.budget_max = parseFloat(budget);
      const res = await fetch(`${API}/api/metaverse/creator-marketplace/match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Matching failed');
      setResult(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const CatIcon = (cat) => CATEGORIES[cat]?.icon || Sparkles;

  return (
    <Dialog open={open} onOpenChange={() => { onClose(); setResult(null); setDescription(''); setBudget(''); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-match-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Wand2 className="w-4 h-4 text-white" />
            </div>
            Bitsy Creator Matchmaker
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Describe what your hotel needs and Bitsy will recommend the best creators and listings from the marketplace.
            </p>

            <div>
              <Label className="text-xs mb-1.5 block">What does your hotel need?</Label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="e.g., We're a boutique heritage hotel in Rajasthan looking for a photorealistic digital twin of our 200-year-old royal suite. We also need NFT welcome gifts for VIP guests..."
                className="w-full h-28 px-3 py-2 rounded-lg border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="match-description-input"
              />
            </div>

            <div>
              <Label className="text-xs mb-1.5 block">Max budget (HERMES, optional)</Label>
              <Input
                type="number"
                value={budget}
                onChange={e => setBudget(e.target.value)}
                placeholder="e.g., 5000"
                data-testid="match-budget-input"
              />
            </div>

            <Button onClick={handleMatch} disabled={loading || !description.trim()} className="w-full h-11" data-testid="find-matches-btn">
              {loading ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                  Bitsy is analyzing...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Find My Perfect Creators
                </>
              )}
            </Button>

            {loading && (
              <div className="text-center py-4">
                <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-600 rounded-full px-4 py-2 text-xs">
                  <MessageSquare className="w-3.5 h-3.5 animate-pulse" />
                  Bitsy is reviewing all creators and their portfolios...
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <Target className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold heading-font text-sm">Bitsy's Recommendation</p>
                  <p className="text-sm text-muted-foreground mt-1">{result.summary}</p>
                </div>
              </div>
            </div>

            {/* Matches */}
            <div className="space-y-3">
              {result.matches?.map((match, i) => {
                const MatchCatIcon = CatIcon(match.creator_category);
                const license = LICENSE_INFO[match.license_type] || LICENSE_INFO.open;
                const LicenseIcon = license.icon;
                return (
                  <div key={match.listing_id || i}
                    className="border border-border/60 rounded-xl p-4 hover:shadow-md transition-shadow"
                    data-testid={`match-result-${i}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                          i === 0 ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                          i === 1 ? 'bg-gradient-to-br from-slate-400 to-slate-500' :
                          'bg-gradient-to-br from-amber-700 to-amber-800'
                        }`}>
                          {match.rank || i + 1}
                        </div>
                        <div>
                          <p className="font-semibold heading-font text-sm">{match.listing_title}</p>
                          <button
                            onClick={() => { onClose(); onViewCreator?.(match.creator_id); }}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <MatchCatIcon className="w-3 h-3" />
                            {match.creator_name}
                            {match.creator_verified && <ShieldCheck className="w-3 h-3 text-teal-500" />}
                          </button>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          <Award className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-sm font-bold heading-font">{match.match_score}%</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground">match</span>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed mb-3">{match.reasoning}</p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] ${license.color}`}>
                          <LicenseIcon className="w-3 h-3 mr-1" />{license.label}
                        </Badge>
                        {match.creator_rating && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Star className="w-3 h-3 text-amber-500" />{match.creator_rating}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold mono text-sm">{match.price_hermes?.toLocaleString()} HERMES</span>
                        <Button size="sm" className="h-8" data-testid={`match-buy-${i}`}
                          onClick={() => {
                            onClose();
                            // Find the full listing object to pass to purchase
                            const fullListing = {
                              listing_id: match.listing_id,
                              title: match.listing_title,
                              creator_name: match.creator_name,
                              creator_verified: match.creator_verified,
                              license_type: match.license_type,
                              price_hermes: match.price_hermes,
                              preview_images: [],
                              category: match.creator_category,
                              limited_copies: null,
                              copies_sold: 0,
                              royalty_pct: 5,
                            };
                            onPurchase(fullListing);
                          }}>
                          Acquire
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button variant="outline" onClick={() => { setResult(null); }} className="w-full" data-testid="match-try-again-btn">
              <Wand2 className="w-4 h-4 mr-2" /> Try Different Description
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// --- MAIN PAGE ---
export default function CreatorMarketplacePage() {
  const [listings, setListings] = useState([]);
  const [creators, setCreators] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [activeTab, setActiveTab] = useState('listings');

  // Purchase dialog
  const [purchaseListing, setPurchaseListing] = useState(null);

  // Creator detail
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [creatorListings, setCreatorListings] = useState([]);

  // AI Matching
  const [showMatcher, setShowMatcher] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') params.set('category', category);
      if (search) params.set('search', search);
      params.set('sort_by', sortBy);

      const [listRes, creatorsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/metaverse/creator-marketplace/listings?${params}`),
        fetch(`${API}/api/metaverse/creator-marketplace/creators${category !== 'all' ? `?category=${category}` : ''}`),
        fetch(`${API}/api/metaverse/creator-marketplace/stats`),
      ]);
      const [listData, creatorsData, statsData] = await Promise.all([
        listRes.json(), creatorsRes.json(), statsRes.json(),
      ]);
      setListings(listData);
      setCreators(creatorsData);
      setStats(statsData);
    } catch (e) {
      console.error('Failed to load marketplace:', e);
    }
    setLoading(false);
  }, [category, search, sortBy]);

  useEffect(() => { loadData(); }, [loadData]);

  // Seed on first load if empty
  useEffect(() => {
    if (!loading && listings.length === 0 && creators.length === 0) {
      fetch(`${API}/api/metaverse/creator-marketplace/seed`, { method: 'POST' })
        .then(() => loadData())
        .catch(() => {});
    }
  }, [loading, listings.length, creators.length, loadData]);

  const viewCreator = async (creatorId) => {
    try {
      const [cRes, lRes] = await Promise.all([
        fetch(`${API}/api/metaverse/creator-marketplace/creators/${creatorId}`),
        fetch(`${API}/api/metaverse/creator-marketplace/creators/${creatorId}/listings`),
      ]);
      const [cData, lData] = await Promise.all([cRes.json(), lRes.json()]);
      setSelectedCreator(cData);
      setCreatorListings(lData);
    } catch (e) {
      toast.error('Failed to load creator profile');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="creator-marketplace-page">
      {/* Hero / Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/80 to-background" />
        </div>
        <nav className="relative border-b border-border/40 bg-background/60 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/metaverse" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-metaverse">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[hsl(var(--web3-primary))] to-[hsl(var(--web3-accent))] flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="font-bold heading-font text-sm">Creator Marketplace</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs mono hidden sm:flex">
                <span className="text-muted-foreground mr-1">Vol:</span>{stats?.total_volume_hermes?.toLocaleString() || 0} HERMES
              </Badge>
              <Link to="/metaverse" data-testid="world-map-link">
                <Button variant="ghost" size="sm" className="text-xs">World Map</Button>
              </Link>
            </div>
          </div>
        </nav>

        <div className="relative max-w-7xl mx-auto px-4 pt-10 pb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold heading-font tracking-tight" data-testid="marketplace-heading">
            BHERMES Creator<br />Marketplace
          </h1>
          <p className="text-muted-foreground mt-3 max-w-lg text-sm md:text-base">
            Acquire digital twins, virtual tours, collectible NFTs, and 360° scans from verified creators. All transactions in $HERMES tokens.
          </p>

          {/* AI Matchmaker CTA */}
          <Button
            onClick={() => setShowMatcher(true)}
            className="mt-5 h-11 px-6 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white border-0 shadow-lg shadow-amber-500/20"
            data-testid="ai-match-btn"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Ask Bitsy to Find Your Creator
          </Button>

          {/* Stats row */}
          {stats && (
            <div className="flex flex-wrap gap-4 mt-6">
              {[
                { label: 'Creators', value: stats.total_creators, icon: Users },
                { label: 'Listings', value: stats.total_listings, icon: Package },
                { label: 'Total Orders', value: stats.total_orders, icon: ShoppingCart },
                { label: 'Protocol Fees', value: `${stats.protocol_fees_hermes} HERMES`, icon: TrendingUp },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-2 bg-card/80 backdrop-blur-sm border border-border/60 rounded-lg px-3 py-2" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <s.icon className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-bold heading-font mono">{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</span>
                  <span className="text-xs text-muted-foreground">{s.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        {/* Tabs + Filters */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <TabsList className="grid w-full sm:w-auto grid-cols-3" data-testid="marketplace-tabs">
              <TabsTrigger value="listings" data-testid="tab-listings">Listings</TabsTrigger>
              <TabsTrigger value="creators" data-testid="tab-creators">Creators</TabsTrigger>
              <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
                <Trophy className="w-3.5 h-3.5 mr-1" />Leaderboard
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search listings..." className="pl-10 h-9"
                  data-testid="marketplace-search-input" />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[130px] h-9" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="price_low">Price: Low</SelectItem>
                  <SelectItem value="popular">Popular</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="category-filters">
            {Object.entries(CATEGORIES).map(([key, cat]) => {
              const Icon = cat.icon;
              return (
                <Button key={key} variant={category === key ? 'default' : 'outline'} size="sm"
                  onClick={() => setCategory(key)} className="h-8 text-xs"
                  data-testid={`category-${key}`}>
                  <Icon className="w-3.5 h-3.5 mr-1.5" />{cat.label}
                </Button>
              );
            })}
          </div>

          {/* Listings Tab */}
          <TabsContent value="listings">
            {loading ? (
              <div className="text-center py-16">
                <Sparkles className="w-8 h-8 text-[hsl(var(--web3-primary))] mx-auto mb-3 animate-pulse" />
                <p className="text-muted-foreground">Loading marketplace...</p>
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No listings found.</p>
                <p className="text-xs text-muted-foreground mt-1">Try adjusting your filters.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="listings-grid">
                {listings.map((l, i) => (
                  <ListingCard key={l.listing_id} listing={l} index={i}
                    onPurchase={setPurchaseListing} onViewCreator={viewCreator} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Creators Tab */}
          <TabsContent value="creators">
            {loading ? (
              <div className="text-center py-16">
                <Sparkles className="w-8 h-8 text-[hsl(var(--web3-primary))] mx-auto mb-3 animate-pulse" />
                <p className="text-muted-foreground">Loading creators...</p>
              </div>
            ) : creators.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No creators found.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="creators-grid">
                {creators.map(c => (
                  <CreatorCard key={c.creator_id} creator={c} onClick={() => viewCreator(c.creator_id)} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard">
            <LeaderboardTab onViewCreator={viewCreator} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Purchase Dialog */}
      <PurchaseDialog listing={purchaseListing} open={!!purchaseListing} onClose={() => { setPurchaseListing(null); loadData(); }} />

      {/* Creator Detail Panel */}
      {selectedCreator && (
        <CreatorDetailPanel creator={selectedCreator} listings={creatorListings}
          onClose={() => setSelectedCreator(null)} onPurchase={(l) => { setSelectedCreator(null); setPurchaseListing(l); }} />
      )}

      {/* AI Matchmaker Dialog */}
      <AiMatchDialog open={showMatcher} onClose={() => setShowMatcher(false)}
        onPurchase={setPurchaseListing} onViewCreator={viewCreator} />
    </div>
  );
}
